import React, { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress, Alert, Grid, Typography, Paper } from '@mui/material';
import theme from '../../../theme';
import SearchBar from './components/SearchBar';
import ProjectTable from './components/ProjectTable';
import ActionButtons from './components/ActionButtons';
import CreateProjectForm from './components/CreateProjectForm';
import { fetchProjectsForUser } from './services/projectService';
import { useNotifications } from '../../../NotificationSystem';
import cookie from 'js-cookie';

const ProjectList = () => {
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
    const [filterOption, setFilterOption] = useState('Active');

    const businessId = cookie.get('businessId');

    // Use notification context for real-time count updates
    const { getMultipleProjectNotificationCounts } = useNotifications();

    // Get current notification counts for all projects
    const currentNotificationCounts = useMemo(() => {
        const projectIds = projects.map((p) => p.id);
        return getMultipleProjectNotificationCounts(projectIds);
    }, [projects, getMultipleProjectNotificationCounts]);

    // Merge projects with current notification counts
    const projectsWithUpdatedCounts = useMemo(() => {
        return projects.map((project) => ({
            ...project,
            notifications: currentNotificationCounts[project.id] || project.notifications || 0,
        }));
    }, [projects, currentNotificationCounts]);

    const loadProjects = async () => {
        try {
            setLoading(true);
            const data = await fetchProjectsForUser();
            setProjects(data);
        } catch (err) {
            console.error('Error loading projects:', err);
            setError('Failed to load projects. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProjects();
    }, []);

    // Apply both search filter and sort/filter options
    const applyFilters = (projectsData, search, filter) => {
        // First apply search filter
        let filtered = projectsData;

        if (search) {
            filtered = filtered.filter((project) =>
                project.name.toLowerCase().includes(search.toLowerCase())
            );
        }

        // Then apply selected filter/sort option
        switch (filter) {
            case 'Active':
                filtered = filtered.filter((project) => project.status === 'Active');
                break;
            case 'DateAsc':
                filtered = [...filtered].sort(
                    (a, b) => new Date(a.rawStartDate) - new Date(b.rawStartDate)
                );
                break;
            case 'DateDesc':
                filtered = [...filtered].sort(
                    (a, b) => new Date(b.rawStartDate) - new Date(a.rawStartDate)
                );
                break;
            case 'Notifications':
                filtered = [...filtered].sort((a, b) => b.notifications - a.notifications);
                break;
            case 'All':
            default:
                // No additional filtering needed
                break;
        }

        setFilteredProjects(filtered);
    };

    useEffect(() => {
        applyFilters(projectsWithUpdatedCounts, searchTerm, filterOption);
    }, [searchTerm, filterOption, projectsWithUpdatedCounts]);

    const handleSearchChange = (value) => {
        setSearchTerm(value);
    };

    const handleFilterChange = (option) => {
        setFilterOption(option);
    };

    const handleCreateProject = () => {
        //TODO: create project form is commented out for now
        //Need to add the form back in when we create the workflow
        setIsCreateFormOpen(true);
    };

    const handleProjectCreated = (newProject) => {
        // Refresh the project list after creating a new project
        loadProjects();
    };

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
            <Grid
                container
                spacing={3}
                sx={{
                    mb: 3,
                    alignItems: 'flex-start',
                }}
            >
                <Grid item xs={12} md={6}>
                    <SearchBar onSearchChange={handleSearchChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <ActionButtons
                        onCreateProject={handleCreateProject}
                        onFilterOption={handleFilterChange}
                        currentFilter={filterOption}
                    />
                </Grid>
            </Grid>

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 8,
                    }}
                >
                    <CircularProgress
                        size={40}
                        thickness={4}
                        sx={{ color: theme.palette.primary.main }}
                    />
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    }}
                >
                    {error}
                </Alert>
            ) : filteredProjects.length === 0 ? (
                <Paper
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                        border: `1px solid ${theme.palette.grey[100]}`,
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            mb: 2,
                            color: theme.palette.grey[700],
                            fontWeight: 600,
                        }}
                    >
                        No projects found
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            mb: 4,
                            color: theme.palette.grey[600],
                            maxWidth: '400px',
                            mx: 'auto',
                        }}
                    >
                        {searchTerm || filterOption !== 'All'
                            ? "Try adjusting your search or filter to find what you're looking for."
                            : "You don't have any projects yet."}
                    </Typography>
                </Paper>
            ) : (
                <Box>
                    <ProjectTable projects={filteredProjects} />
                </Box>
            )}

            <CreateProjectForm
                open={isCreateFormOpen}
                onClose={() => setIsCreateFormOpen(false)}
                onProjectCreated={handleProjectCreated}
                businessId={businessId}
            />
        </Box>
    );
};

export default ProjectList;
